// ============================================
// SCORE CARD COMPONENT (V3 COMPATIBLE)
// Main score display with status indicator
// ============================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TickerAnalysis, ScannerContext } from '../types';
import { TrendingDown, TrendingUp, Rocket } from 'lucide-react';

interface ScoreCardProps {
  analysis: TickerAnalysis;
}

export const ScoreCard: React.FC<ScoreCardProps> = ({ analysis }) => {
  const navigate = useNavigate();
  const { ticker, marketData, scores, status, statusReason, analyzedAt } = analysis;
  
  // Use normalizedTotal (0-100 scale) for display
  const finalScore = scores.normalizedTotal;

  // Determine score color based on V3 thresholds
  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-emerald-400';
    if (score >= 50) return 'text-amber-400';
    return 'text-rose-400';
  };
  
  // Determine status styling
  const getStatusStyle = () => {
    switch (status) {
      case 'TRADE':
        return {
          bg: 'bg-emerald-500/10',
          border: 'border-emerald-500/30',
          text: 'text-emerald-400',
          glow: 'shadow-emerald-500/20',
          icon: '✓',
        };
      case 'CAUTION':
        return {
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/30',
          text: 'text-amber-400',
          glow: 'shadow-amber-500/20',
          icon: '⚠',
        };
      default:
        return {
          bg: 'bg-rose-500/10',
          border: 'border-rose-500/30',
          text: 'text-rose-400',
          glow: 'shadow-rose-500/20',
          icon: '✕',
        };
    }
  };
  
  const statusStyle = getStatusStyle();
  const scoreColor = getScoreColor(finalScore);
  
  // Format time
  const time = new Date(analyzedAt).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Calculate price changes
  const currentPrice = marketData.price;
  const prevClose = marketData.prevClose || currentPrice;
  const priceChange = currentPrice - prevClose;
  const priceChangePercent = prevClose > 0 ? ((priceChange / prevClose) * 100) : 0;
  const isUp = priceChange >= 0;

  // Determine if we're in pre-market / after-hours
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const dayOfWeek = now.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const isPreMarket = !isWeekend && hours >= 4 && hours < 9.5; // 4:00 AM - 9:30 AM
  const isAfterHours = !isWeekend && (hours >= 16 || hours < 4); // 4:00 PM - 4:00 AM
  const isExtendedHours = isPreMarket || isAfterHours || isWeekend;
  const marketSession = isPreMarket ? 'PRE-MARKET' : isAfterHours || isWeekend ? 'AFTER-HOURS' : 'LIVE';

  // Navigate to Moonshot with scanner context
  const handleAskMoonshot = () => {
    const strategy = analysis.bestStrategy;
    const riskFlagsArray = [
      ...(analysis.riskFlags?.warningFlags || []),
      ...(analysis.riskFlags?.criticalFlags || [])
    ];

    const scannerContext: ScannerContext = {
      ticker,
      currentPrice,
      score: finalScore,
      status,
      statusReason,
      direction: strategy?.direction || 'NEUTRAL',
      strategyName: strategy?.name || 'Analysis',
      legs: strategy?.legs || [],
      maxProfit: strategy?.maxProfit || 0,
      maxLoss: strategy?.maxLoss || 0,
      breakeven: strategy?.breakeven || 0,
      probabilityOfProfit: strategy?.probabilityOfProfit || 0,
      riskRewardRatio: strategy?.riskRewardRatio || 0,
      creditDebit: strategy?.creditDebit || 'DEBIT',
      warnings: strategy?.warnings || [],
      ivRank: analysis.optionsData?.ivRank || 0,
      riskFlags: riskFlagsArray,
      stepScores: {
        step1_sentiment: { score: scores.step1_sentiment.points, max: scores.step1_sentiment.maxPoints, passed: scores.step1_sentiment.passed },
        step2_technicals: { score: scores.step2_technicals.points, max: scores.step2_technicals.maxPoints, passed: scores.step2_technicals.passed },
        step3_options: { score: scores.step3_options.points, max: scores.step3_options.maxPoints, passed: scores.step3_options.passed },
        step4_risk: { score: scores.step4_risk.points, max: scores.step4_risk.maxPoints, passed: scores.step4_risk.passed },
        step5_greeks: { score: scores.step5_greeks.points, max: scores.step5_greeks.maxPoints, passed: scores.step5_greeks.passed },
        step6_entry: { score: scores.step6_entry.points, max: scores.step6_entry.maxPoints, passed: scores.step6_entry.passed },
        step7_review: { score: scores.step7_review?.points || 0, max: scores.step7_review?.maxPoints || 10, passed: scores.step7_review?.passed || true },
        step8_sizing: { score: scores.step8_sizing.points, max: scores.step8_sizing.maxPoints, passed: scores.step8_sizing.passed },
      },
      volEdge: analysis.volMetrics?.volEdge,
      volEdgeSignal: analysis.volMetrics?.edgeSignal,
      volEdgeStrength: analysis.volMetrics?.edgeStrength,
      regime: analysis.volMetrics?.regime?.state,
      regimeConfidence: analysis.volMetrics?.regime?.confidence,
      zScore: analysis.volMetrics?.zScore20d,
      ewmaForecast: analysis.volMetrics?.ewmaForecast,
      rv20d: analysis.volMetrics?.rv20d,
      analyzedAt: analysis.analyzedAt,
    };
    navigate('/moonshot', { state: { scannerContext } });
  };

  return (
    <div className="bg-terminal-card rounded-xl border border-terminal-border overflow-visible">
      {/* Header with Ticker and Status */}
      <div className="px-6 py-3 border-b border-terminal-border bg-terminal-bg/50">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-black text-terminal-bright tracking-tight">
            ${ticker}
          </h2>
          {/* Status Badge */}
          <div className={`
            px-4 py-2 rounded-lg border ${statusStyle.bg} ${statusStyle.border}
            flex items-center gap-2 shadow-lg ${statusStyle.glow}
          `}>
            <span className={`text-lg ${statusStyle.text}`}>{statusStyle.icon}</span>
            <span className={`font-bold ${statusStyle.text}`}>{status}</span>
          </div>
        </div>
      </div>

      {/* Dual Price Display */}
      <div className="px-4 sm:px-6 py-4 space-y-3 border-b border-terminal-border">
        {/* Current / Pre-Market Price */}
        <div className={`p-3 rounded-lg ${isUp ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <span className={`text-2xl sm:text-3xl font-black ${isUp ? 'text-emerald-400' : 'text-rose-400'}`}>
              ${currentPrice.toFixed(2)}
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              {isUp ? <TrendingUp size={18} className="text-emerald-400" /> : <TrendingDown size={18} className="text-rose-400" />}
              <span className={`font-bold ${isUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                {isUp ? '+' : ''}{priceChange.toFixed(2)}
              </span>
              <span className={`px-2 py-0.5 rounded text-sm font-bold ${isUp ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                {isUp ? '+' : ''}{priceChangePercent.toFixed(2)}%
              </span>
            </div>
          </div>
          <div className="text-xs sm:text-sm text-slate-300 mt-1 uppercase font-bold">
            {marketSession}: {time} EST
          </div>
        </div>

        {/* Previous Close */}
        <div className="p-3 rounded-lg bg-slate-800/50">
          <div className="flex items-center justify-between">
            <span className="text-xl font-bold text-slate-300">
              ${prevClose.toFixed(2)}
            </span>
            <span className="text-sm text-slate-300 uppercase font-bold">
              Previous Close
            </span>
          </div>
        </div>

        {/* Data Status Notice - simple, clean */}
        {marketData.isDelayed ? (
          <div className="text-sm text-amber-400/70 text-center py-1">
            ⚠️ Data may be delayed up to 15 min
          </div>
        ) : (
          <div className="text-sm text-emerald-400/70 text-center py-1">
            ● Real-time data
          </div>
        )}
      </div>
      
      {/* Score Display */}
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4">
          <div className="flex items-end gap-2">
            <span className={`text-5xl sm:text-6xl font-black ${scoreColor} tabular-nums`}>
              {finalScore}
            </span>
            <span className="text-xl sm:text-2xl text-terminal-dim mb-1 sm:mb-2">/100</span>
          </div>
          <button
            onClick={handleAskMoonshot}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm transition-all shadow-lg hover:shadow-purple-500/30"
          >
            <Rocket size={18} />
            Ask Moonshot
          </button>
        </div>
        
        {/* Score Bar */}
        <div className="relative h-3 bg-terminal-border rounded-full overflow-hidden mb-4">
          <div 
            className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${
              finalScore >= 70 
                ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' 
                : finalScore >= 50 
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-500'
                  : 'bg-gradient-to-r from-rose-500 to-red-600'
            }`}
            style={{ width: `${finalScore}%` }}
          />
          
          {/* 70 threshold marker */}
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-terminal-bright/50"
            style={{ left: '70%' }}
          />
        </div>
        
        {/* Threshold Labels */}
        <div className="flex justify-between text-xs text-terminal-dim mb-6">
          <span>0</span>
          <span className="text-amber-400">50 (Caution)</span>
          <span className="text-emerald-400">70 (Trade)</span>
          <span>100</span>
        </div>
        
        {/* Status Reason */}
        <div className={`
          p-3 rounded-lg border text-sm
          ${statusStyle.bg} ${statusStyle.border}
        `}>
          <p className={statusStyle.text}>{statusReason}</p>
        </div>
        
        {/* Quick Score Breakdown */}
        <div className="mt-6 space-y-2">
          {[
            { label: 'Sentiment', score: scores.step1_sentiment },
            { label: 'Technicals', score: scores.step2_technicals },
            { label: 'Liquidity', score: scores.step3_options },
            { label: 'Risk Check', score: scores.step4_risk },
            { label: 'Greeks', score: scores.step5_greeks },
            { label: 'Entry/Exit', score: scores.step6_entry },
            { label: 'Sizing', score: scores.step8_sizing } // Added Step 8
          ].map((item, i) => (
            <div key={i} className="flex justify-between text-xs border-b border-slate-800/50 pb-1 last:border-0">
              <span className="text-terminal-dim">{item.label}</span>
              <div className="flex gap-1">
                <span className={`font-bold ${item.score.points >= item.score.maxPoints * 0.7 ? 'text-emerald-400' : 'text-amber-400'}`}>
                   {item.score.points}
                </span>
                <span className="text-terminal-dim">/{item.score.maxPoints}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
